/**
 * Models workspace slide body (list + detail). Extracted so Panel stays under
 * the file-size hard cap while group authoring wires through ListColumn.
 */
import { useState } from "react";
import type { MfgMaterial, MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingModelsDetailColumn } from "./ManufacturingModelsDetailColumn";
import { ManufacturingModelsPriceChangePanel } from "./ManufacturingModelsPriceChangePanel";
import { ManufacturingModelsListColumn } from "./ManufacturingModelsListColumn";
import type { useManufacturingModelsAttachmentSection } from "./useManufacturingModelsAttachmentSection";
import type { useManufacturingModelsBulkSelection } from "./useManufacturingModelsBulkSelection";
import type { useManufacturingModelsListView } from "./useManufacturingModelsListView";
import type { useManufacturingModelsTab } from "./useManufacturingModelsTab";

type ListView = ReturnType<typeof useManufacturingModelsListView>;
type Bulk = ReturnType<typeof useManufacturingModelsBulkSelection>;
type ModelsTab = ReturnType<typeof useManufacturingModelsTab>;
type PackageAttachments = ReturnType<typeof useManufacturingModelsAttachmentSection>;

export function ManufacturingModelsModelsSlide({
  isCreate,
  m,
  listView,
  bulk,
  materials,
  packageAttachments,
  pendingCounts,
  canEditCatalogCode,
  canPermanentlyDelete,
  canDeactivateModel,
  selectedRecipe,
  orgSlotLabels,
  onDictationComplete,
  onRequestDeactivate,
  onRequestPermanentDeleteFromList,
  onRequestPermanentDeleteSelected,
  onDuplicate,
  onBulkApplied,
  onConfirmReview,
  confirmBusy,
  refreshPendingCounts,
  onSlotLabelsRefresh,
  onOpenVariantsWorkspace,
  onOpenPartUses,
  onReviewConfirmed,
  onOpenBomSandbox,
  onToast,
}: {
  isCreate: boolean;
  m: ModelsTab;
  listView: ListView;
  bulk: Bulk;
  materials: MfgMaterial[];
  packageAttachments: PackageAttachments;
  pendingCounts: Record<string, number>;
  canEditCatalogCode: boolean;
  canPermanentlyDelete: boolean;
  canDeactivateModel: boolean;
  selectedRecipe: MfgRecipeSummary | null;
  orgSlotLabels: readonly string[];
  onDictationComplete: (transcript: string) => void;
  onRequestDeactivate: () => void;
  onRequestPermanentDeleteFromList: (recipe: MfgRecipeSummary) => void;
  onRequestPermanentDeleteSelected: () => void;
  onDuplicate: (id: string) => void;
  onBulkApplied: () => void | Promise<void>;
  onConfirmReview: (ids: string[]) => void;
  confirmBusy?: boolean;
  refreshPendingCounts: () => void;
  onSlotLabelsRefresh: () => void;
  onOpenVariantsWorkspace: (namedVariantId?: string) => void;
  onOpenPartUses: () => void;
  onReviewConfirmed: () => void;
  onOpenBomSandbox: (groupId?: string) => void;
  onToast?: (message: string) => void;
}) {
  const [priceOpen, setPriceOpen] = useState(false);
  if (priceOpen) {
    return (
      <ManufacturingModelsPriceChangePanel
        onClose={() => setPriceOpen(false)}
        onCommitted={() => {
          void onBulkApplied();
        }}
      />
    );
  }
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ManufacturingModelsListColumn
        isCreate={isCreate}
        recipes={m.recipes}
        listView={listView}
        bulk={bulk}
        selectedId={m.selectedId}
        creating={m.creating}
        listBusy={m.listBusy}
        pendingCounts={pendingCounts}
        canEditCatalogCode={canEditCatalogCode}
        canDeactivateModel={canDeactivateModel}
        canPermanentlyDelete={canPermanentlyDelete}
        onPick={m.pickRecipe}
        onDuplicate={onDuplicate}
        onRequestPermanentDelete={onRequestPermanentDeleteFromList}
        onBulkApplied={onBulkApplied}
        onConfirmReview={onConfirmReview}
        confirmBusy={confirmBusy}
        onOpenBomSandbox={onOpenBomSandbox}
        onOpenPriceChange={() => {
          m.closeSelectedModel();
          setPriceOpen(true);
        }}
        onToast={onToast}
      />

      <ManufacturingModelsDetailColumn
        isCreate={isCreate}
        m={m}
        materials={materials}
        recipes={m.recipes}
        recipeGroups={listView.recipeGroups}
        packageAttachments={packageAttachments}
        canDeactivateModel={canDeactivateModel}
        canPermanentlyDelete={canPermanentlyDelete}
        canEditCatalogCode={canEditCatalogCode}
        selectedRecipe={selectedRecipe}
        onDictationComplete={onDictationComplete}
        onRequestDeactivate={onRequestDeactivate}
        onRequestPermanentDelete={onRequestPermanentDeleteSelected}
        refreshPendingCounts={refreshPendingCounts}
        orgSlotLabels={orgSlotLabels}
        onSlotLabelsRefresh={onSlotLabelsRefresh}
        onOpenVariantsWorkspace={onOpenVariantsWorkspace}
        onOpenPartUses={onOpenPartUses}
        pendingCount={m.selectedId ? pendingCounts[m.selectedId] ?? 0 : 0}
        onReviewConfirmed={onReviewConfirmed}
      />
    </div>
  );
}
